import React, { useState } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { Check } from '@mui/icons-material';

import Config from 'arbitrip-common/client/utils/Config';
import { Dates as DateUtils } from 'arbitrip-common/client/utils';
import PriceBreakdown from 'arbitrip-common/client/utils/PriceBreakdown';
import {
    isDealAboveBnplMinimum,
    isBnplChargeDateEligible,
    getBnplChargeDateFromCancellation,
} from 'arbitrip-common/client/utils/BnplUtils';
import { dealFacts } from 'arbitrip-common/client/utils/HotelDealsRm3';

import DealConstants from '@/constants/DealConstants';
import { isIsracardCompany } from '@/utils/isCompany';
import BadgeOfferIsracard from '@/components/general/BadgeOfferIsracard';
import { ReactComponent as FreeCancelationIcon } from '@/img/results_page/free_cancelation_icon.svg';

import Rm3Ribbon from './Rm3Ribbon';
import Rm3DealPrice from './Rm3DealPrice';
import Rm3BookAction from './Rm3BookAction';
import { IconMeal, IconNonRefundable, IconRoomOnly } from './Rm3Icons';
import { BOARD_LABEL_KEYS } from './rm3BoardLabels';

const DEAL_LOG_PREFIX = 'S: ';
const BNPL_DATE_FORMAT = 'DD MMM YYYY';

function FreeCancellationLine({ till }) {
    const { t, i18n } = useTranslation();
    return (
        <div className="rm3-terms-line" data-testid="deal-cancellation-item" data-free-cancellation="true">
            <FreeCancelationIcon className="rm3-terms-line__icon rm3-terms-line__icon--free" />
            <span>
                <strong>{t('FreeCancellation')}</strong> {t('Before')}{' '}
                <strong className="rm3-terms-line__date">
                    {DateUtils.formatCancellationDeadline(till, i18n.language)}
                </strong>
            </span>
        </div>
    );
}

function NonRefundableLine() {
    const { t } = useTranslation();
    return (
        <div className="rm3-terms-line" data-testid="deal-cancellation-item" data-free-cancellation="false">
            <span className="rm3-terms-line__icon">
                <IconNonRefundable />
            </span>
            <span>{t('NonRefundable')}</span>
        </div>
    );
}

/** `DealComponent.getCancellation` + its `CANCEL_POLICY` rule: shown only after recheck (`deal.chooseprod`). */
function CancellationLine({ deal }) {
    const { t } = useTranslation();
    const dealStatus = deal.status;
    if (dealStatus === DealConstants.DEAL_STATUS.FAIL || dealStatus === DealConstants.DEAL_STATUS.NA) {
        return (
            <div className="rm3-terms-line">
                <i className="arbi-icon fa fa-ban" aria-hidden="true" title={deal.error} />
                <span> {t('ThisDealIsNotAvailable')}</span>
            </div>
        );
    }
    if (!deal.id || !deal.chooseprod) {
        return null;
    }
    if (deal.chooseprod.dca.nonRefundable) {
        return <NonRefundableLine />;
    }
    return <FreeCancellationLine till={deal.chooseprod.dca.dealIsFullyRefundableTill} />;
}

function BoardLine({ deal }) {
    const { t } = useTranslation();
    const { boardKey, hasMealPlan } = dealFacts(deal);
    return (
        <div className="rm3-terms-line" data-testid="rm3-deal-board" data-board={boardKey}>
            <span className="rm3-terms-line__icon">{hasMealPlan ? <IconMeal /> : <IconRoomOnly />}</span>
            <span className={hasMealPlan ? 'rm3-terms-line__meal' : undefined}>{t(BOARD_LABEL_KEYS[boardKey])}</span>
        </div>
    );
}

/** `DealComponent.getBnplTag`, verbatim rules and copy. */
function BnplLine({ deal, profile }) {
    const { t } = useTranslation();
    const bnplEnabled =
        _.get(profile, 'settings.bnpl_enabled', false) || _.get(profile, 'company.settings.bnpl_enabled', false);
    if (!bnplEnabled) return null;

    const nonRefundable = _.get(deal, 'chooseprod.dca.nonRefundable', true);
    if (nonRefundable) return null;

    const cancellationDate = _.get(deal, 'chooseprod.dca.dealIsFullyRefundableTill');
    if (!cancellationDate) return null;

    const settings = _.get(profile, 'company.settings');
    if (!isBnplChargeDateEligible(cancellationDate, settings)) return null;

    if (!isDealAboveBnplMinimum(deal, settings)) return null;

    const chargeDate = getBnplChargeDateFromCancellation(cancellationDate, settings).format(BNPL_DATE_FORMAT);
    const bnplAdditionalCostApplies = _.get(deal, 'chooseprod.dca.bnpl_additional_cost_applies', false);
    return (
        <div className="deal-bnpl-tag">
            <Check />
            <span>
                {t('HotelPage.BnplDealTag', { date: chargeDate })}
                {bnplAdditionalCostApplies && ` ${t('HotelPage.BnplAdditionalCost')}`}
            </span>
        </div>
    );
}

function logDeal(deal) {
    if (Config.dev_mode) {
        console.log('deal: ', deal);
        const sentence = _.get(deal, 'chooseprod.dca.cancellationPoliciesText');
        if (sentence) {
            console.log('new cancellation sentence: ' + sentence);
        }
    }
    // As `DealComponent.handleOnDealInfoWrapperClick`: we always want a printout of this deal identifier.
    console.log(DEAL_LOG_PREFIX + deal.id);
}

/**
 * One bookable row: ribbon, cancellation, board, BNPL, then today's price block and Book action. The price
 * column sits inside a `.deals-wrapper .deal .deal-info` scope so `_dealComponent.scss` styles the ported
 * markup exactly as on the legacy list; `hotelDealsRm3.scss` only neutralises that scope's card chrome.
 */
function Rm3DealRow({ deal, ribbon, roomName, displayedOrder, context }) {
    const [hovered, setHovered] = useState(false);
    const { profile, basket, travelPolicy, arbitripPointsApplied, contractClientCompanyId, priceState, infoBusy } =
        context;
    const faultDeal = deal && deal.faultDeal;

    let dealInfoClasses = 'deal-info flex';
    if (PriceBreakdown.isBreakdownPrice({ profile, deal })) {
        dealInfoClasses += ' expanded';
    }

    return (
        <div
            className={`rm3-deal${hovered && !faultDeal ? ' rm3-deal--hovered' : ''}`}
            data-testid="rm3-deal"
            data-deal-id={deal.id}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onClick={() => logDeal(deal)}
        >
            <div className="rm3-deal__terms">
                {ribbon ? <Rm3Ribbon kind={ribbon} /> : null}
                <CancellationLine deal={deal} />
                <BoardLine deal={deal} />
                <BnplLine deal={deal} profile={profile} />
            </div>
            <div className="rm3-deal__price deals-wrapper rm3-price-scope">
                <div className="deal single">
                    <div className={dealInfoClasses}>
                        <div className="deal-price-wrap rm3-price-wrap">
                            <div className="rm3-price-main">
                                {isIsracardCompany() && (
                                    <BadgeOfferIsracard
                                        style={{ marginBottom: '4px' }}
                                        data-testid="isracard-offer-badge"
                                    />
                                )}
                                <Rm3DealPrice
                                    deal={deal}
                                    profile={profile}
                                    travelPolicy={travelPolicy}
                                    arbitripPointsApplied={arbitripPointsApplied}
                                    contractClientCompanyId={contractClientCompanyId}
                                    priceState={priceState}
                                />
                            </div>
                            <Rm3BookAction
                                deal={deal}
                                profile={profile}
                                basket={basket}
                                roomName={roomName}
                                hovered={hovered}
                                addToListDisabled={infoBusy}
                                onBook={(bookedDeal) => context.book(bookedDeal, displayedOrder)}
                                onAddToList={context.addToList}
                                onRemoveFromList={context.removeFromList}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Rm3DealRow;
